import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea,
  Cell,
  Tooltip
} from "recharts";

/**
 * Decision Pressure vs Readiness Quadrant Chart
 * 
 * Four quadrants:
 * - Foundation Building (low readiness, low pressure)
 * - Strategic Position (high readiness, low pressure)
 * - Attention Required (low readiness, high pressure)
 * - Action Zone (high readiness, high pressure)
 */
const PressureReadinessChart = ({ data }) => {
  if (!data) return null;

  const readiness = data.x_axis?.value || 50;
  const pressure = data.y_axis?.value || 50;
  
  const pointData = [{ x: readiness, y: pressure }];

  // Quadrant colors (muted for executive appearance)
  const quadrantColors = {
    "Foundation Building": "#1e3a5f",
    "Strategic Position": "#1e4d3a",
    "Attention Required": "#4d3a1e",
    "Action Zone": "#3a4d1e"
  };

  // Common chart content
  const renderChartContent = (isPrint = false) => (
    <>
      {/* Quadrant backgrounds */}
      <ReferenceArea x1={0} x2={50} y1={0} y2={50} fill={isPrint ? "#e2e8f0" : "#1e3a5f"} fillOpacity={isPrint ? 0.5 : 0.3} />
      <ReferenceArea x1={50} x2={100} y1={0} y2={50} fill={isPrint ? "#d1fae5" : "#1e4d3a"} fillOpacity={isPrint ? 0.5 : 0.3} />
      <ReferenceArea x1={0} x2={50} y1={50} y2={100} fill={isPrint ? "#fef3c7" : "#4d3a1e"} fillOpacity={isPrint ? 0.5 : 0.3} />
      <ReferenceArea x1={50} x2={100} y1={50} y2={100} fill={isPrint ? "#dcfce7" : "#3a4d1e"} fillOpacity={isPrint ? 0.5 : 0.3} />
      
      <CartesianGrid strokeDasharray="3 3" stroke={isPrint ? "#94a3b8" : "#334155"} />
      
      <XAxis 
        type="number" 
        dataKey="x" 
        domain={[0, 100]} 
        name="Readiness"
        tick={{ fill: isPrint ? '#374151' : '#94a3b8', fontSize: 10 }}
        label={{ value: 'Readiness →', position: 'bottom', fill: isPrint ? '#374151' : '#94a3b8', fontSize: 11 }}
      />
      <YAxis 
        type="number" 
        dataKey="y" 
        domain={[0, 100]} 
        name="Pressure"
        tick={{ fill: isPrint ? '#374151' : '#94a3b8', fontSize: 10 }}
        label={{ value: 'Pressure →', angle: -90, position: 'insideLeft', fill: isPrint ? '#374151' : '#94a3b8', fontSize: 11 }}
      />
      
      {/* Center dividing lines */}
      <ReferenceLine x={50} stroke={isPrint ? "#6b7280" : "#475569"} strokeDasharray="5 5" />
      <ReferenceLine y={50} stroke={isPrint ? "#6b7280" : "#475569"} strokeDasharray="5 5" />
      
      <Scatter name="Position" data={pointData} fill="#d97706">
        {pointData.map((entry, index) => (
          <Cell 
            key={`cell-${index}`} 
            fill="#d97706"
            stroke={isPrint ? "#1e293b" : "#fff"}
            strokeWidth={2}
          />
        ))}
      </Scatter>
    </>
  );

  return (
    <div className="w-full" data-testid="pressure-readiness-chart">
      <div className="mb-4">
        <h3 className="font-heading text-lg font-semibold text-white print:text-slate-900">
          {data.title || "Decision Pressure vs. Readiness"}
        </h3>
        <p className="text-slate-400 print:text-slate-600 text-sm">{data.subtitle}</p>
      </div>
      
      {/* Screen version */}
      <div className="h-[320px] w-full print:hidden">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 20, right: 30, bottom: 30, left: 30 }}>
            {renderChartContent(false)}
            <Tooltip
              contentStyle={{
                backgroundColor: '#1e293b',
                border: '1px solid #334155',
                borderRadius: '8px',
                color: '#fff'
              }}
              formatter={(value, name) => [value, name === 'x' ? 'Readiness' : 'Pressure']}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      {/* Print version with fixed dimensions */}
      <div className="hidden print:block w-full" style={{ height: '280px' }}>
        <ScatterChart 
          width={400} 
          height={280} 
          margin={{ top: 20, right: 30, bottom: 30, left: 40 }}
          style={{ margin: '0 auto' }}
        >
          {renderChartContent(true)}
        </ScatterChart>
      </div>
      
      {/* Quadrant Result */}
      <div className="mt-4 p-4 bg-navy-950/50 print:bg-slate-100 rounded-lg border border-slate-700 print:border-slate-300">
        <div className="flex items-center gap-3 mb-2">
          <div className="px-3 py-1 rounded-full text-sm font-medium print:bg-slate-200 print:text-slate-800" style={{ 
            backgroundColor: `${quadrantColors[data.quadrant]}99`,
            color: '#fff'
          }}>
            {data.quadrant}
          </div>
        </div>
        <p className="text-slate-300 print:text-slate-700 text-sm">{data.quadrant_description}</p>
        
        {/* Score breakdown */}
        <div className="flex gap-6 mt-3 pt-3 border-t border-slate-700 print:border-slate-300">
          <div>
            <span className="text-slate-500 print:text-slate-600 text-xs uppercase tracking-wider">Readiness</span>
            <p className="text-primary print:text-amber-700 font-heading font-bold text-lg">{readiness}<span className="text-slate-500 print:text-slate-600 text-sm">/100</span></p>
          </div>
          <div>
            <span className="text-slate-500 print:text-slate-600 text-xs uppercase tracking-wider">Pressure</span>
            <p className="text-primary print:text-amber-700 font-heading font-bold text-lg">{pressure}<span className="text-slate-500 print:text-slate-600 text-sm">/100</span></p>
          </div>
        </div>
      </div>

      {/* Quadrant Legend (for print) */}
      <div className="hidden print:grid grid-cols-2 gap-2 mt-3 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: '#e2e8f0' }}></div>
          <span className="text-slate-600">Foundation Building</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: '#d1fae5' }}></div>
          <span className="text-slate-600">Strategic Position</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: '#fef3c7' }}></div>
          <span className="text-slate-600">Attention Required</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: '#dcfce7' }}></div>
          <span className="text-slate-600">Action Zone</span>
        </div>
      </div>
    </div>
  );
};

export default PressureReadinessChart;
